/**
 * Intake submit payload builders — pure-TS ports of the legacy
 * `__submitAudience` / `__submitMirror` / `__submitBody` / `utmQuery`,
 * decoupled from the DOM: they take a typed FormState instead of reading
 * document elements.
 *
 * The mirror payload targets **Projects (Mirror)** in the SFMC Sandbox base —
 * the realistic twin of production Projects (prod-exact field names + select
 * options; see legacy docs FIELD-MAP-PRODUCTION.md + EMAIL-REQUEST-FLOW.md).
 * Production cutover = the worker repoints one config; these field names are
 * the production contract.
 */
import type { SubmitBody } from "../types/api";
import { messageText } from "./requestData";
import { visualSpec } from "./visualSpec";
import { echParentCommunity } from "./echDyn";
import type { AnyBlockInstance } from "../email/document";
import { NONPROD_HOST } from "./api";

/* ── A NON-PROD SUBMIT IS A TEST, AND SAYS SO (2026-09-17) ─────────────────────────────
   Justin Bradford, Teams 09-17: "Please make sure you are putting TEST in front of any tests
   you send in. If TEST is not added the jobs automatically get added to the current queue and
   to my list to be built out." Two QA rehearsals (09-16, 09-17) reached his live queue under
   the auto-suggested name because the suggestion filled the field after the tester's "TEST"
   was typed. The backend already routes a Project Name that STARTS WITH the word "test" (or
   `test: true`) to the dev intake hook (submit.controller hookTarget) — so the fix is to make
   every dev / QA / UAT submit carry both, whatever the field says. Production is untouched: a
   real requester's name is never prefixed. localhost is deliberately NOT stamped — the jsdom
   suite runs there and asserts the plain names. */
export function isTestSubmit(): boolean {
  try { return typeof window !== "undefined" && NONPROD_HOST.test(window.location.hostname); } catch { return false; }
}
/** "TEST " in front of a name unless it already starts with the word test */
export function testStamp(name: string): string {
  const n = String(name || "").trim();
  if (!isTestSubmit()) return n;
  return /^test\b/i.test(n) ? n : "TEST " + n;
}

/**
 * The masterplan behind a community pick, when the requester never picked one outright.
 *
 * Reads the FIRST pick only — the same first-of-kind rule the form's hidden `#masterplan`
 * has always used — and returns "" unless the parent is genuinely a different place from
 * the pick. `echParentCommunity("Granny Park")` returns "Granny Park", and filing that as
 * a masterplan would state a relationship nobody asserted.
 */
/*
 * ── A DERIVED NAME SHIPS ONLY WHEN IT IS A REAL MASTERPLAN (2026-09-08, Katy) ─────────
 * UTH test: the requester picked the collection "Canyons - The Valley At Wildflower" and
 * no masterplan; the row carried `Masterplan Name: "Canyons"` with no `Masterplan —
 * Record ID`. "Canyons" is the collection. `echParentCommunity` is a NAME heuristic — it
 * reads the two halves of "X - Y" and picks the one that does not look like a collection
 * — and here "The Valley At Wildflower" starts with "The", which the heuristic reads as a
 * collection marker, so it returned the wrong half. Katy links Masterplan from the record
 * id, never from the text, so a name without an id errors her integrity check and the
 * submission cannot route to Projects: "make sure that it sends nothing when no masterplan
 * is selected. Just not a name without an ID."
 *
 * So the derived parent is checked against the masterplans the division actually HAS
 * (`known` — the same list the Step-1 picker offers, from /refdata with the bundled
 * snapshot as fallback). A parent that is one of them resolves to an id on the backend and
 * ships; anything else — a wrong half, a plain community, a collection — ships as "".
 * Her 2026-08-25 fail-safe ("I do need the master plan name coming in") still holds for
 * every collection whose parent is a real masterplan, which is the case it was asked for.
 * No list ⇒ no guess: callers that cannot say what the masterplans are get "".
 */
export function mpFallback(community?: string, known?: readonly string[]): string {
  const first = String(community || "").split(",")[0].trim();
  if (!first) return "";
  const parent = echParentCommunity(first);
  if (!parent || parent.toLowerCase() === first.toLowerCase()) return "";
  const want = parent.toLowerCase();
  const hit = (known || []).find((k) => String(k || "").trim().toLowerCase() === want);
  return hit ? String(hit).trim() : "";
}

/**
 * IS THE DEDICATED LOGO-LINK COLUMN LIVE IN AIRTABLE? 2026-08-31.
 *
 * TRUE as of 2026-08-31: created on Projects (Mirror) (`tblfzhHRrdWeJeYK6` in
 * `appzOFSAIzFpovvIl`) as `fld9ZrYAr0SYZYnRr`, type url, and read back — which is the
 * table `buildMirror` below actually writes to.
 *
 * It stays a flag rather than becoming unconditional because the write uses
 * `typecast:false`, where ONE unknown key rejects the whole 177-field row: if this row is
 * ever pointed at a base lacking the column, this is the switch that keeps submissions
 * working. **Production is a separate base** (`appJHKUeeWfEZNhxR`) — the column has to
 * exist there too before the mirror is promoted.
 */
export const LOGO_LINK_COLUMN = true;

export interface UtmParts {
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
  adgroupname: string;
}

/** legacy utmQuery — the full GA tagging string appended to every real CTA at send */
export function utmQuery(u: UtmParts): string {
  return `utm_source=${u.source}&utm_medium=${u.medium}&utm_campaign=${u.campaign}&utm_term=${u.term}&utm_content=${u.content}&utm_adgroupname=${u.adgroupname}`;
}

export interface AudienceSnapshot {
  community?: string;
  audiences?: string;
  audType?: string;
  opportunityScope?: string;
  buyerProfile?: string;
  areaOfInterest?: string;
  leadStatus?: string[];
  createdWindow?: string;
  buyerTimeframe?: string[];
  priceRange?: string[];
  productType?: string;
  bedrooms?: string;
  mustHaves?: string[];
  engagement?: string;
  websiteActive?: boolean;
  suppressionWindows?: string[];
  estimatedReach?: number;
}

export interface AudienceSummary {
  criteria: string | null;
  suppressions: string | null;
  estReach: number | null;
  audType: string | null;
  /* Carried through 2026-08-19 so buyerTypes() can reach it. It was previously folded into
     the criteria PROSE only, which meant the buyer-type column had no structured source and
     the port fell back to the audience code — the bug that rejected the whole mirror row. */
  buyerProfile: string | string[] | null;
}

/** legacy __submitAudience — plain-English criteria block for the intake row */
export function audienceSummary(a: AudienceSnapshot): AudienceSummary {
  const line = (k: string, v: unknown): string | null => {
    if (v == null) return null;
    let s: string;
    if (Array.isArray(v)) {
      if (!v.length) return null;
      s = v.join(", ");
    } else s = String(v);
    s = s.trim();
    return s ? k + ": " + s : null;
  };
  const crit = [
    line("Community", a.community), line("Audiences", a.audiences), line("Opportunity Scope", a.opportunityScope),
    line("Buyer Profile", a.buyerProfile), line("Area of Interest", a.areaOfInterest), line("Lead Status", a.leadStatus),
    line("Created Window", a.createdWindow), line("Buyer Timeframe", a.buyerTimeframe), line("Price Range", a.priceRange),
    line("Product", a.productType), line("Bedrooms", a.bedrooms), line("Must-Haves", a.mustHaves),
    line("Engagement", a.engagement), a.websiteActive ? "Website Active: yes" : null,
  ].filter(Boolean).join("\n");
  return {
    criteria: crit || null,
    suppressions: (a.suppressionWindows || []).join(", ") || null,
    estReach: typeof a.estimatedReach === "number" ? a.estimatedReach : null,
    audType: a.audType || null,
    buyerProfile: a.buyerProfile || null,
  };
}

/** the form's own state — what the 4-step UI collects (legacy pay.airtable.fields + computed) */
export interface FormState {
  projectName: string;
  divisionCode: string;
  requestedByEmail: string;
  market?: string | string[];
  masterplan?: string;
  community?: string;
  requestType?: string;
  /** Initiative Request Focus: Division | Market | Area of Interest | Community */
  reqFocus?: string;
  areaOfInterest?: string;
  initiative?: string;
  template?: string | string[];
  subject?: string;
  preheader?: string;
  headline?: string;
  primaryMessage?: string;
  disclaimer?: string;
  /** Step 4 Disclaimer answers (2026-10-02, services/disclaimer.ts) — what the template and
   *  the email can't answer on their own. UI state only: not in the submit body yet. */
  dsAnswers?: { eventFood?: string; accoladeType?: string; accoladeOrg?: string; accoladeYear?: string; sqftType?: string; added?: string[]; removed?: string[] };
  /** The built disclaimer (2026-10-14, Katy). Filled by FormView at submit from the same
   *  paragraphs it writes into the footer, so the column always matches the email. Sent as
   *  `Disclaimer Text` — never under `Disclaimer`, which carries the Builder request # only.
   *  (`dsDetail` / `Disclaimer Builder Detail` was dropped 2026-10-23 at Katy's request; the
   *  field stays optional so an old caller can't break the build, and is ignored.) */
  dsText?: string;
  dsDetail?: string;
  referenceFiles?: string;
  anythingElse?: string;
  targetDueDate?: string;          // YYYY-MM-DD
  targetDeploy?: string;           // "YYYY-MM-DD HH:MM"
  emailAudienceType?: string;
  senderType?: string;
  /** UTM Targeting — buyer profile the send is tagged against (drives utm_term) */
  utmTarget?: string;
  senderProfile?: string;
  sfListsInclude?: string;
  sfListsExclude?: string;
  specialtyBranding?: string;
  /** ── THE LOGO LIVES IN BOX, NOT BYNDER (2026-08-28 standup) ───────────────────
   *  Specialty-branded communities have a logo, and it is not in the DAM — Layne's
   *  team keeps them in Box. MVP answer: the requester pastes the Box link, and it
   *  travels with the request. Full Bynder integration is milestone 2. */
  specialtyLogoUrl?: string;
  approvedExperiment?: boolean;
  /** primaryMessage holds the select CODE ("CS"); payloads carry messageText(code) —
   *  the monolith submits `$("message").options[selectedIndex].text` (L4588). */
  /** naming-engine output (UTM 2026 conventions) */
  emailName?: string;
  utm?: UtmParts;
  /** first Studio module labels (mirror carries 3) */
  studioModules?: string[];
  /**
   * Production record ids held client-side. DELIBERATELY NARROW: the WORKER resolves
   * every entity id server-side from refdata — see MIRROR_ALLOW's "Record IDs — worker
   * resolves these server-side (7)" in submit.controller.ts, and `put()` there only
   * writes a non-empty value. A 2026-08-31 attempt to resolve and send all seven from
   * the client was reverted for that reason: it was dead weight that implied the
   * frontend owns ids it does not.
   */
  prodRecordIds?: { template?: string; initiative?: string };
}

const j = (v?: string | string[]) => (Array.isArray(v) ? v.join(", ") : v || "");

/**
 * Audience-code → exact production option string. Ported from the monolith's __AUD_MAP.
 *
 * The Step-3 builder holds a bare CODE ("LO"); the Airtable column's options are the full
 * "CODE = Label" strings. Sending the raw code used to mint a junk "LO" option via
 * typecast — and now that typecast is off it hard-fails the write instead.
 */
const AUD_MAP: Record<string, string> = {
  C: "C = Customer", H: "H = Homeowners", B: "B = Buyers", HB: "HB = Owners & Buyers",
  L: "L = Leads", O: "O = Opportunities", LO: "LO = Leads & Opportunities",
  R: "R = Real Estate Professional", LOR: "LOR = Leads & Opp & Real Estate Pro",
  A: "A = Associates", I: "I = Investor",
};

/**
 * PRIMARY MESSAGE → THE EXACT AIRTABLE OPTION (2026-08-30).
 *
 * MEASURED on a live dev submit (`recx62Up8ksgfCtjf`): the worker answered ok, the screen
 * went green, and the response carried
 *   `droppedSelects: [{ field: "Primary Message (Editable) (Used in UTM 2026)",
 *                       value: "QMI = Quick Move-in Home (No offer)" }]`
 * — the column is a singleSelect, that string is not one of its options, so the value was
 * discarded and the row landed with NO primary message. Nothing told the requester.
 *
 * QMI was not alone. Diffing `messageText()` against the live option list (41 choices,
 * field `fldu0QRWa4l50DEnW`, read 2026-08-30) found FOURTEEN of the twenty-nine messages
 * unmatched — mostly punctuation and the trailing "(offer)" the app appends:
 *
 *   QMI      "Quick Move-in Home (No offer)"  vs  "Move-in Ready Home"
 *   OC       "Buy Now / Online Checkout"      vs  "Buy Now/Online Checkout"      (spaces)
 *   PhR      "Phase / Sales Release"          vs  "Phase Release/Sales Release"
 *   Tour     "Tours / Appointments"           vs  "Tour/Appointment"
 *   SwGv     "Sweepstakes / Giveaways"        vs  "Sweepstakes/Giveaways"
 *   55       "55+ / Active Adult"             vs  "55+"
 *   NG       code "NG"                        vs  option code "NextGen"
 *   SenI     "SenI = Seasonal / Holiday"      vs  "Senl = Seasonal/Holiday"      (l vs I!)
 *   21BD · 1BD · 76ARM · FC · MthPay · Opts   — the app appends " (offer)", the option has none
 *
 * So roughly half of all sends were losing their message theme. The text columns
 * (`Copy Input`, `Message Theme`) are free text and keep the human label — only this
 * singleSelect is mapped, and an unmapped code returns "" rather than a guess, because
 * with `typecast:false` a wrong value costs the WHOLE row rather than one cell.
 */
const MESSAGE_MAP: Record<string, string> = {
  CS: "CS = Coming Soon", ComOv: "ComOv = Community Overview", GO: "GO = Grand Opening",
  FtFp: "FtFp = Featured Floorplan", QMI: "QMI = Move-in Ready Home",
  HOTW: "HOTW = Home of the Week", OC: "OC = Buy Now/Online Checkout",
  PhR: "PhR = Phase Release/Sales Release", FO: "FO = Final Opportunity",
  Tour: "Tour = Tour/Appointment", SEvt: "SEvt = Special Event", Web: "Web = Webinar",
  SwGv: "SwGv = Sweepstakes/Giveaways", "55": "55 = 55+", Ei: "Ei = Everything's Included",
  NG: "NextGen = Next Gen", Ref: "Ref = Referral", SenI: "Senl = Seasonal/Holiday",
  Invstr: "Invstr = Investor", CstCre: "CstCre = Customer Care",
  FxdRate: "FxdRate = Fixed Rate (offer)", "321BD": "321BD = 3-2-1 Buydown Rate (offer)",
  "21BD": "21BD = 2-1 Buydown Rate", "1BD": "1BD = 1 Buydown Rate", "76ARM": "76ARM = 7/6 ARM",
  CC: "CC = Closing Costs (offer)", FC: "FC = Flex Credit",
  MthPay: "MthPay = Monthly Payment", Opts: "Opts = Options/Upgrades",
};
/** the singleSelect value for a message code — "" when unmapped, never a guess */
export const messageOption = (code: string): string => MESSAGE_MAP[String(code || "").trim()] || "";

/**
 * The Disclaimer Builder request NUMBER, or "" — see the "Disclaimer" field below.
 *
 * Katy's column links to a table keyed on the numeric request id, so anything else mints a
 * junk linked record rather than failing loudly. A bare number passes; a leading "#" or
 * "DB" prefix and surrounding whitespace are stripped because requesters type them; and a
 * string carrying more than one number ("DB-2026-0909-JAX-GP50-FP") is DROPPED, because
 * there is no safe way to choose which run of digits was meant.
 *
 * NO LEADING ZEROS AND NO ZERO (Farhan's review, 2026-08-31): in a linked table keyed on
 * the number, "0533" is a DIFFERENT string from "533" — it would mint a junk record, the
 * exact failure this function exists to prevent. And "0" is not a request number.
 */
export function disclaimerId(raw: unknown): string {
  const s = String(raw ?? "").trim().replace(/^#\s*/, "").replace(/^DB[-\s]*/i, "").trim();
  return /^[1-9]\d{0,9}$/.test(s) ? s : "";
}

/** The 14 real Target Audience/Buyer Type options, verified live off the Projects schema. */
const BUYER_TYPES = [
  "Active Adult/55+", "Broker", "Couples", "First-time Homebuyer", "Investor",
  "Move-up Buyer", "Multi-family", "Next Gen", "Relocation", "Renters",
  "Suburban", "Urban/City-dweller", "Young Families", "All Buyer Types",
];
/** Buyer-profile aliases → the exact option name. Ported from the monolith's __BUYER_MAP. */
const BUYER_MAP: Record<string, string> = {
  FTHB: "First-time Homebuyer", "First Time": "First-time Homebuyer",
  "First-time Homebuyer": "First-time Homebuyer",
  MU: "Move-up Buyer", MoveUp: "Move-up Buyer", "Move Up": "Move-up Buyer",
  "Move-up Buyer": "Move-up Buyer",
  AA: "Active Adult/55+", ActiveAdult: "Active Adult/55+", "Active Adult": "Active Adult/55+",
  "55+": "Active Adult/55+", "Active Adult/55+": "Active Adult/55+",
  Investor: "Investor", Broker: "Broker", Renters: "Renters", Relocation: "Relocation",
  NextGen: "Next Gen", "Next Gen": "Next Gen", Couples: "Couples", Suburban: "Suburban",
  "Young Families": "Young Families", "Multi-family": "Multi-family",
  "All Buyer Types": "All Buyer Types", "Urban/City-dweller": "Urban/City-dweller",
  /* ── THE PERSONA CODES AND LABELS THE AUDIENCE BUILDER ACTUALLY SENDS (2026-08-28) ──
     `segToAudienceSnapshot` used to send the LABEL, and three of the four never
     matched — "First-Time Homebuyer" (capital T), "55-Plus" and "Market / All" all
     fell through the filter and shipped an empty column. It sends the CODE now; these
     entries make both spellings land, so a payload built before that change, or by
     anything else that passes a label, still resolves. */
  Market: "All Buyer Types", "Market / All": "All Buyer Types",
  "First-Time Homebuyer": "First-time Homebuyer",
  "55-Plus": "Active Adult/55+",
  /* the two UTM Targeting values with no persona of their own (2026-09-08, A2): a
     National or Community send is not aimed at one buyer type, and the UTM builder
     already treats both as Market. */
  National: "All Buyer Types", Community: "All Buyer Types",
};

/**
 * Buyer types as an ARRAY of whitelisted option names — port of the monolith's __buyerTypes().
 *
 * Two bugs this fixes, both observed live on emailbuilder-dev 2026-08-19:
 *  1. the column is multipleSelects and was being sent a bare STRING, so Airtable answered
 *     "Cannot parse value for field Target Audience/Buyer Type (used in UTM 2026)" and the
 *     ENTIRE mirror row was rejected — while the form still reported "Filed in Airtable";
 *  2. the value sent was the Step-3 AUDIENCE code ("LO"), a different taxonomy from this
 *     column's buyer types. Nothing in the 14 options is a code.
 *
 * Returns [] when nothing maps; the worker omits empty values rather than writing junk.
 */
function buyerTypes(aud: AudienceSummary): string[] {
  const raw: string[] = [];
  const bp = aud.buyerProfile;
  if (bp) raw.push(...(Array.isArray(bp) ? bp : [bp]));
  const out: string[] = [];
  for (const v of raw) {
    const s = String(v == null ? "" : v).trim();
    if (!s) continue;
    const mapped = BUYER_MAP[s] || s;
    if (BUYER_TYPES.includes(mapped) && !out.includes(mapped)) out.push(mapped);
  }
  return out;
}

/**
 * Split the combined deploy value into the three raw keys the intake automation maps.
 * Port of the monolith's __deployParts(). The zone is fixed Eastern, matching the monolith:
 * the deploy time the division types is always read as Lennar Eastern, and stating the zone
 * explicitly is the whole point — a bare "9:00" was previously parsed as browser-local while
 * being labelled EDT, so the same entry meant different instants in different offices.
 */
const DEPLOY_TZ = "America/New_York";
function deployParts(targetDeploy?: string): { deployDate: string; deployTime: string; deployTimeZone: string } {
  const m = String(targetDeploy || "").match(/^(\d{4}-\d{2}-\d{2})[T\s]+(\d{1,2}:\d{2})/);
  return m
    ? { deployDate: m[1], deployTime: m[2], deployTimeZone: DEPLOY_TZ }
    : { deployDate: "", deployTime: "", deployTimeZone: "" };
}

/** legacy __submitMirror — the production-Projects-shaped row */
/** what the submit knows beyond the form — today, the division's masterplan names */
export interface SubmitKnown { masterplans?: readonly string[] }

export function buildMirror(s: FormState, aud: AudienceSummary, known: SubmitKnown = {}): Record<string, unknown> {

  /* RUSH: real prod values (RUSH - AM / RUSH - PM / empty) — due inside 3 days, AM/PM from deploy time */
  let rush = "";
  if (s.targetDueDate) {
    const ms = new Date(s.targetDueDate + "T12:00:00").getTime() - Date.now();
    if (ms >= 0 && ms < 3 * 864e5) {
      let hh = 12;
      const tm = String(s.targetDeploy || "").match(/\s(\d{1,2}):/);
      if (tm) hh = parseInt(tm[1], 10);
      rush = hh < 12 ? "RUSH - AM" : "RUSH - PM";
    }
  }
  /* deploy → ISO for the dateTime field */
  let depIso: string | null = null;
  const m = String(s.targetDeploy || "").match(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2})/);
  if (m) {
    const d = new Date(m[1] + "T" + m[2] + ":00");
    if (!isNaN(d.getTime())) depIso = d.toISOString();
  }
  const mods = s.studioModules || [];
  const ids = s.prodRecordIds || {};
  return {
    "Project Name": testStamp(s.projectName || "Email Request"),
    "Division Code": s.divisionCode || "",
    "Submitted by Email": s.requestedByEmail || "",
    "Target Due Date": s.targetDueDate || null,
    "RUSH": rush,
    "Project Type": "Email",
    "Email Template Request Type": s.requestType || "",
    "Email Nurture Template": j(s.template),
    /* SEPARATE, NOT MERGED (2026-08-19, Katy: "can Communities and Masterplan be separated
       instead of combined? If a community is linked to a masterplan, the naming already covers
       that relationship, so we don't need them merged into one field").

       "Market/Masterplan/Community Name" used to carry all three joined by " · " while
       Communities and Masterplan Name ALSO shipped separately — so the merged one was pure
       duplication for anyone mapping fields, except for Market, which had no column of its own
       and existed ONLY inside that string. The worker even resolved Market — Record ID by
       parsing the string's first segment, which is why the merged field could not just be
       deleted: doing that alone would have silently broken market id resolution.

       So Market is now its own field, and the merged column is gone. */
    "Communities": s.community || "",
    "Market": j(s.market),
    /* MASTERPLAN NAME, WITH A FALLBACK (2026-08-25, Katy on the ECH standup: "I do need the
       master plan name coming in as a fail safe — I have the record, which is great, but in
       case I ever have to look at something I have to hunt for this master plan record").

       It only ever filled from an explicit MP chip (`cmSel` kind "MP"). A division that picks
       a COLLECTION instead — which is the normal way a masterplan send gets scoped, Airtable
       names them "50s - Granary Park" — left this blank, so the row named a collection and
       never named the place. Nothing else on the row recovers it.

       `echParentCommunity` is the same resolver the Listing Table already uses to head a
       section with the community rather than the collection, so the fallback agrees with what
       the email itself prints. Only used when it actually RESOLVES something different from
       the pick: a plain community ("Granny Park") returns itself, and writing that as a
       masterplan would invent a relationship that does not exist.

       Safe by construction: the field is TEXT (fldGMt8GJIyBo0i7V), and the worker only sets
       "Masterplan — Record ID" when the name matches a real masterplan (`if (hit.length)`), so
       a derived name that is not one lands as a readable label and resolves to no link — it
       cannot 422 the write the way an unmapped SELECT would. */
    "Masterplan Name": s.masterplan || mpFallback(s.community, known.masterplans),
    /* AREA OF INTEREST as its own column (2026-08-19, Katy). Previously the AOI survived only
       inside the Audience Criteria prose, so a job scoped to an AOI had nothing on the row
       saying which one — and "Area of Interest" became a selectable Initiative Request Focus
       when the focus picklist went live off the schema on 2026-08-18. The worker resolves the
       matching record id from /refdata's ids.areas; this is the display text it matches on, so
       it has to be sent even though the id is filled server-side. */
    "Area of Interest": j(s.areaOfInterest),
    "Subject Line": s.subject || "",
    "Pre-Header Text": s.preheader || "",
    "Headline": s.headline || "",
    /* FALLBACK ONLY (2026-08-24). visualSpec overwrites this with the copy blocks the
       division actually wrote; this keeps a bare intake — no Studio build — from
       filing an empty Copy Input. Message Theme below is the same string under the
       name Katy's mapping expects, so the theme survives even when real copy lands
       here and the two stop being the same value. */
    "Copy Input": messageText(s.primaryMessage || ""),
    "Message Theme": messageText(s.primaryMessage || ""),
    /* ── SUBMISSION ID — the dedupe key (2026-08-24) ─────────────────────────────
       On the worker's ALLOW list since 2026-08-14, described there as the dedupe
       key, and never sent by ANY client — so it has been blank on every intake row
       and Katy flagged it 2026-08-22. Deliberately DERIVED, not random: a dedupe
       key has to collide when the same logical request arrives twice (a double
       click, a retried submit, a division re-filing the same job), which a UUID
       per attempt could never do. Division + email name + deploy instant is the
       business key that identifies one send. */
    "Submission ID": [
      (s.divisionCode || "").toUpperCase(),
      s.emailName || s.projectName || "",
      String(s.targetDeploy || s.targetDueDate || ""),
    ].map((x) => String(x).trim()).filter(Boolean).join(" | "),
    "Module 1": mods[0] || "", "Module 2": mods[1] || "", "Module 3": mods[2] || "",
    /* ── THE DISCLAIMER IS A NUMBER, NOT A LABEL (2026-08-31, Katy) ──────────────────
       "Disclaimer needs to go back to the numeric ID. It's come through as `533`, then
       full body text, now `DB-2026-0909-JAX-GP50-FP`. My field links to a table with a
       numeric primary, so string codes create junk records instead of matching."

       Her column is a LINK to a table whose primary key is the Disclaimer Builder request
       number. A non-numeric value there does not fail the write — it mints a new record
       named after whatever string arrived, which is worse than an empty cell: it looks
       matched and points at nothing. Three different shapes reached her in a week (a bare
       number, pasted body copy, and a prefixed code), so the field is normalised HERE
       rather than trusted from the form.

       Digits only, first run wins: "DB-2026-0909-JAX-GP50-FP" → "2026" would be wrong, so
       a value that is not a clean request number is dropped rather than guessed — an empty
       cell costs her one lookup, a junk linked record costs her a cleanup. */
    "Disclaimer": disclaimerId(s.disclaimer),
    /* the Box logo link rides in the reference-files column — it IS a reference file,
       and inventing a new Airtable column would 422 the whole 144-field write
       (`typecast:false`). Labelled so Creative can see what it is (2026-08-28). */
    /* ── THE LOGO LINK'S OWN COLUMN (2026-08-31, ATL pilot) ─────────────────────────
       Ashley asked for a dedicated "Masterplan / Community Logo Link" field. The column
       exists on Projects (Mirror) as of 2026-08-31; `LOGO_LINK_COLUMN` above is the
       switch, because with `typecast:false` one unknown key rejects the entire row. */
    ...(LOGO_LINK_COLUMN && String(s.specialtyLogoUrl || "").trim()
      ? { "Masterplan/Community Logo Link": String(s.specialtyLogoUrl).trim() }
      : {}),
    "Existing Template/Reference Files": [
      s.referenceFiles || "",
      !LOGO_LINK_COLUMN && String(s.specialtyLogoUrl || "").trim() ? "Logo (Box): " + String(s.specialtyLogoUrl).trim() : "",
    ].filter(Boolean).join("\n"),
    "Anything Else": s.anythingElse || "",
    "Target Deployment Date/Time": depIso,
    /* UNCROSSED 2026-08-19. The note that used to sit here claimed the crossing WAS the
       production convention and cited monolith L4539–4540 — but those were the buggy lines.
       The monolith fixed this on 2026-08-13 with the comment: "these two were crossed.
       Audience Type had been receiving Initiative Request Focus (Market/Community/AOI) and
       Sender Type had been receiving the audience code (LO/H/B), while the real Sender Type
       value was collected and discarded." The port carried the pre-fix behaviour forward.

       Proof it was wrong, not just inconsistent: production's Sender Type options are
       Market / MarketSpanish / Masterplan / Community / City / National / Mkt-Promo /
       Comty-Promo — "LO" is not among them, so the old mapping could only ever have
       survived by minting a junk option through typecast. `s.senderType` is the real
       control (SendScope drives it off the live 8-option picklist) and was being discarded. */
    "Email Audience Type (for UTM 2026)": AUD_MAP[String(s.emailAudienceType || "").trim()] || "",
    "Sender Type": s.senderType || "",
    "Initiative": s.initiative || "",
    "Sender Profile": s.senderProfile || "",
    /* the SELECT takes the exact option name, not the app's label — see MESSAGE_MAP */
    "Primary Message (Editable) (Used in UTM 2026)": messageOption(s.primaryMessage || ""),
    "Email Name (Editable) (UTM 2026) (utm_content 2026)": s.emailName || "",
    "Google Analytics Tagging": s.utm ? utmQuery(s.utm) : "",
    "Salesforce List(s) to Include": s.sfListsInclude || "",
    "Salesforce List(s) to Exclude": [s.sfListsExclude || "", aud.suppressions || ""].filter(Boolean).join(" · "),
    "Target Audience/Buyer Type (used in UTM 2026)": buyerTypes(aud),
    "Specialty Branding": s.specialtyBranding || "",
    /* FLAG, NOT A YES/NO (2026-08-19). Production's Experiment column has exactly ONE
       option: "NTEXP". Both clients were sending "Yes"/"No", neither of which exists, so
       this was a fourth value that would fail the row the same way the buyer-type column
       did. It has been passing only because the sandbox mirror still carries junk Yes/No
       options minted by the old typecast:true behaviour — production's list is clean, so
       this would have broken at cutover.
       NOTE: the CloudPage monolith (index.html:5266) still sends "Yes"/"No" and needs the
       same one-line change on its next publish. */
    "Experiment": s.approvedExperiment ? "NTEXP" : "",
    "Audience Criteria": aud.criteria || "",
    "Est. Reach": typeof aud.estReach === "number" ? aud.estReach : null,
    "Prod Record IDs": ids.template || ids.initiative ? JSON.stringify(ids) : "",
  };
}

/**
 * legacy __submitBody — the POST /sfmc-submit payload.
 * `html` = the built email (Studio build or loaded template); "" files a bare
 * intake (row writes, no Content Builder asset, no preview).
 */
export function buildSubmitBody(s: FormState, aud: AudienceSnapshot, html: string, doc?: AnyBlockInstance[], known: SubmitKnown = {}): SubmitBody {
  const sum = audienceSummary(aud);
  return {
    project: testStamp(s.projectName || "Email Request"),
    ...(isTestSubmit() ? { test: true } : {}),
    div: s.divisionCode || "",
    division: s.divisionCode || "",
    requestedBy: s.requestedByEmail || "Form submission",
    /* Tool name, renamed 2026-08-19. This is the value the intake's Source column
       shows, NOT a field name the backend keys off — the KEY is still `source`. */
    source: "Email Creative Hub",
    requestType: s.requestType || "",
    mainMessage: messageText(s.primaryMessage || ""),
    template: j(s.template),
    subject: s.subject || "",
    preheader: s.preheader || "",
    note:
      "Community: " + (s.community || "—") +
      " · Market: " + (j(s.market) || "—") +
      (s.anythingElse ? " · " + s.anythingElse : "") +
      (s.emailName ? " · " + s.emailName : ""),
    html,
    /* monolith audience = __submitAudience(): nullable values + the audType code */
    audience: { criteria: sum.criteria, suppressions: sum.suppressions, estReach: sum.estReach, audType: sum.audType },
    /* VISUAL SPEC merged over the base mirror (2026-08-20). visualSpec() returns only the
       keys it has values for, so an absent block never blanks a column — and it wins on the
       fields it owns (Block Order, the named image slots, per-block content) because it reads
       the actual doc rather than the form's summary inputs. */
    /* ── THE FORM'S DISCLAIMER ID SURVIVES THE FOOTER BLOCK (2026-08-31, Farhan) ──────
       visualSpec wins the merge, and it writes `Disclaimer` from the footer block's legal
       text UNCONDITIONALLY (`put("Disclaimer", footer.note)`), so any design containing a
       repo footer silently replaced the requester's Disclaimer Builder number with
       boilerplate. Katy's timeline is exactly this: `533` (clean, no footer) → full legal
       text (the footer's default clobbering it) → `DB-2026-0909-JAX-GP50-FP` (a string the
       input accepted, then recycled by Duplicate A Past Job).

       Fixed HERE rather than in visualSpec because visualSpec cannot see the form — it is
       handed the document only, and the rule is about precedence between two sources, which
       is a property of the merge.

       SUPERSEDED IN PART, 2026-09-01: visualSpec no longer writes `Disclaimer` at all, so
       this line is now belt-and-braces rather than the fix. The "does NOT restore on blank"
       reasoning that used to follow here was wrong — Katy's column links into a table whose
       primary is a NUMBER, so the footer's legal text was minting a junk record on every
       no-number submission, not "legitimately filling" anything. `Disclaimer` is a request
       number or "", from ONE writer (`buildMirror`), and nothing else touches it. */
    mirror: (() => {
      const m: Record<string, unknown> = { ...buildMirror(s, sum, known), ...(doc && doc.length ? visualSpec(doc) : {}) };
      const reqNo = disclaimerId(s.disclaimer);
      if (reqNo) m["Disclaimer"] = reqNo;
      /* KATY, Teams 2026-10-13: "the built disclaimer can go to the existing Disclaimer field.
         Please send it under a new payload key rather than 'Disclaimer', which already carries
         the builder number." Two keys, both plain text; `Disclaimer` is untouched. */
      const dsText = String(s.dsText || "").trim();
      if (dsText) m["Disclaimer Text"] = dsText;
      /* `Disclaimer Builder Detail` is no longer sent — Katy, Teams 2026-10-22: "this isn't
         needed in the payload and can be dropped." */
      return m;
    })(),
    /* RAW DEPLOY KEYS (2026-08-19). Email ops' intake automation lists deployDate /
       deployTime / deployTimeZone as missing: "Target Deployment Date/Time arrives combined,
       in UTC". The monolith has always sent all three (__deployParts, index.html:4840); the
       port never did, so a submit from this app delivered them EMPTY even after the worker
       was taught to pass them through.

       Sent at the TOP LEVEL, deliberately not inside `mirror` — the worker forwards the whole
       body to the intake webhook but ALLOW-filters `mirror` to Airtable column names, and with
       typecast off an unknown key there would fail the entire row write. */
    ...deployParts(s.targetDeploy),
    emailName: s.emailName || "",
    utmq: s.utm ? utmQuery(s.utm) : "",
    status: "Coming Soon",
    reviewStatus: "Needs Review",
  };
}
